import React from 'react';
import { clsx } from 'clsx';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  selectSize?: 'small' | 'medium' | 'large';
  startAdornment?: React.ReactNode;
  options?: { value: string | number; label: string }[];
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth,
      selectSize = 'medium',
      startAdornment,
      options,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const selectClasses = clsx(
      'border rounded transition-all duration-200 appearance-none bg-white',
      'focus:outline-none focus:ring-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'pr-10', // Space for dropdown icon
      {
        // Size variants
        'px-3 py-1.5 text-sm': selectSize === 'small',
        'px-4 py-2 text-base': selectSize === 'medium',
        'px-5 py-3 text-lg': selectSize === 'large',

        // Full width
        'w-full': fullWidth,

        // Error state
        'border-error text-error focus:ring-error-500': error,
        'border-gray-300 text-gray-900 focus:ring-primary-500': !error,

        // Start adornment padding
        'pl-10': startAdornment && selectSize === 'small',
        'pl-12': startAdornment && selectSize === 'medium',
        'pl-14': startAdornment && selectSize === 'large',
      },
      className
    );

    return (
      <div className={clsx('flex flex-col gap-1', { 'w-full': fullWidth })}>
        {label && (
          <label className={clsx('text-sm font-medium', {
            'text-error': error,
            'text-gray-700': !error,
          })}>
            {label}
          </label>
        )}
        
        <div className="relative">
          {startAdornment && (
            <div className={clsx(
              'absolute left-0 top-1/2 -translate-y-1/2 flex items-center pointer-events-none',
              {
                'left-3': selectSize === 'small',
                'left-4': selectSize === 'medium',
                'left-5': selectSize === 'large',
              }
            )}>
              {startAdornment}
            </div>
          )}

          <select
            ref={ref}
            className={selectClasses}
            {...props}
          >
            {options ? (
              options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            ) : (
              children
            )}
          </select>

          {/* Dropdown icon */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {helperText && (
          <p className={clsx('text-xs', {
            'text-error': error,
            'text-gray-500': !error,
          })}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;

